import { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useDataProvider } from 'react-admin';

type IdType = string | number;

export interface UsersPhoneAutocompleteProps {
    label?: string;
    role?: string;
    deleted?: boolean;
    onChange?: (id: IdType | null, item?: any) => void;
    initialValueId?: IdType | null;
    fullWidth?: boolean;
    required?: boolean;
}

const renderUserLabel = (choice: any) => {
    if (!choice) return '';
    const firstName = choice?.firstName || '';
    const lastName = choice?.lastName || '';
    const phone = choice?.phone || 'No Phone';
    const name = [firstName, lastName].filter(Boolean).join(' ');
    const parts = [name || 'Unnamed', phone];
    if (choice?.email) {
        parts.push(choice.email);
    }
    return parts.join(' | ');
};

export const UsersPhoneAutocomplete = ({
    label = 'User by phone',
    role = 'STUDENT',
    deleted = false,
    onChange,
    fullWidth = false,
    required = false,
}: UsersPhoneAutocompleteProps) => {
    const dataProvider = useDataProvider();

    const [searchText, setSearchText] = useState<string>('');
    const [choices, setChoices] = useState<any[]>([]);
    const [selectedItem, setSelectedItem] = useState<any | null>(null);
    const debounceRef = useRef<number | undefined>(undefined);

    const fetchUsers = async (query: string) => {
        try {
            const res: any = await dataProvider.getList('users', {
                pagination: { page: 1, perPage: 50 },
                sort: { field: 'created_at', order: 'DESC' },
                filter: query ? { phone: query, role, deleted } : { role, deleted },
            });
            const dataArray = res?.data || [];
            setChoices(prev => {
                if (selectedItem && !dataArray.find((i: any) => i?.id === selectedItem?.id)) {
                    return [selectedItem, ...dataArray];
                }
                return dataArray;
            });
        } catch (e) {
            console.error('Failed to fetch users', e);
            setChoices([]);
        }
    };

    useEffect(() => {
        fetchUsers('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInputChange = (_: any, value: string) => {
        setSearchText(value);
        if (debounceRef.current) window.clearTimeout(debounceRef.current);

        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length === 10) {
            debounceRef.current = window.setTimeout(() => fetchUsers(value), 400);
        }
    };

    const handleChange = (_: any, value: any | null) => {
        setSelectedItem(value);
        if (onChange) onChange(value?.id ?? null, value ?? undefined);
    };

    const options = useMemo(() => choices, [choices]);

    return (
        <Autocomplete
            options={options}
            value={selectedItem}
            fullWidth={fullWidth}
            onChange={handleChange}
            getOptionLabel={(opt: any) => renderUserLabel(opt)}
            onInputChange={handleInputChange}
            renderInput={params => (
                <TextField {...params} label={label} fullWidth={fullWidth} required={required} />
            )}
        />
    );
};

export default UsersPhoneAutocomplete;
